import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  QrCode, Search, CheckCircle2, AlertTriangle, XCircle, Camera, CameraOff,
  RotateCcw, User, Keyboard, History, WifiOff, Wifi, RefreshCw,
  Maximize, Minimize, Flashlight, SwitchCamera, X
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const RESULT_STYLES = {
  authorized: { icon: CheckCircle2, bg: "bg-[#10B981]", label: "ACCES AUTORISE", color: "#10B981" },
  already_checked: { icon: AlertTriangle, bg: "bg-[#F59E0B]", label: "DEJA ENREGISTRE", color: "#F59E0B" },
  unauthorized: { icon: XCircle, bg: "bg-red-500", label: "ACCES REFUSE", color: "#EF4444" },
  invalid: { icon: XCircle, bg: "bg-red-500", label: "QR INVALIDE", color: "#EF4444" },
  cancelled: { icon: XCircle, bg: "bg-red-500", label: "INSCRIPTION ANNULEE", color: "#EF4444" },
  offline_ok: { icon: CheckCircle2, bg: "bg-[#10B981]", label: "HORS LIGNE — VALIDE", color: "#10B981" },
  offline_dup: { icon: AlertTriangle, bg: "bg-[#F59E0B]", label: "HORS LIGNE — DEJA SCANNE", color: "#F59E0B" },
};

// ===== OFFLINE STORAGE HELPERS =====
const CACHE_KEY = "glotech_scanner_cache";
const QUEUE_KEY = "glotech_scanner_queue";
const OFFLINE_SCANS_KEY = "glotech_offline_scans";

function getCache() { try { return JSON.parse(localStorage.getItem(CACHE_KEY) || "{}"); } catch { return {}; } }
function setCache(data) { localStorage.setItem(CACHE_KEY, JSON.stringify(data)); }
function getQueue() { try { return JSON.parse(localStorage.getItem(QUEUE_KEY) || "[]"); } catch { return []; } }
function setQueue(q) { localStorage.setItem(QUEUE_KEY, JSON.stringify(q)); }
function getOfflineScans() { try { return JSON.parse(localStorage.getItem(OFFLINE_SCANS_KEY) || "[]"); } catch { return []; } }
function setOfflineScans(s) { localStorage.setItem(OFFLINE_SCANS_KEY, JSON.stringify(s)); }

export default function OrgScannerPage() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [qrInput, setQrInput] = useState("");
  const [scanResult, setScanResult] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [history, setHistory] = useState([]);
  const [mode, setMode] = useState("camera");
  const [cameraActive, setCameraActive] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const [scanCount, setScanCount] = useState({ total: 0, authorized: 0, rejected: 0 });
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [offlineQueue, setOfflineQueue] = useState(getQueue());
  const [syncing, setSyncing] = useState(false);
  const [cacheReady, setCacheReady] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [torchOn, setTorchOn] = useState(false);
  const [facingMode, setFacingMode] = useState("environment");
  const inputRef = useRef(null);
  const html5QrRef = useRef(null);
  const lastScanRef = useRef({ data: "", time: 0 });
  const containerRef = useRef(null);

  // Debounce: prevent multiple reads of same QR within 3 seconds
  const SCAN_COOLDOWN = 3000;

  // Monitor connectivity
  useEffect(() => {
    const onLine = () => setIsOnline(true);
    const offLine = () => setIsOnline(false);
    window.addEventListener("online", onLine);
    window.addEventListener("offline", offLine);
    return () => { window.removeEventListener("online", onLine); window.removeEventListener("offline", offLine); };
  }, []);

  // Pre-cache participants for offline mode
  const cacheParticipants = useCallback(async () => {
    try {
      const { data: events } = await axios.get(`${API}/api/event/events`, { headers });
      const evts = Array.isArray(events) ? events : events.events || [];
      const cache = {};
      for (const evt of evts) {
        try {
          const { data: participants } = await axios.get(`${API}/api/event/events/${evt.id}/participants`, { headers });
          const parts = Array.isArray(participants) ? participants : participants.participants || [];
          for (const p of parts) {
            if (p.qr_token) {
              cache[`${evt.id}:${p.qr_token}`] = {
                first_name: p.first_name, last_name: p.last_name,
                reference: p.reference, category: p.category,
                is_vip: p.is_vip, status: p.status,
                event_id: evt.id, event_title: evt.title,
              };
            }
          }
        } catch {}
      }
      setCache(cache);
      setCacheReady(true);
      const count = Object.keys(cache).length;
      if (count > 0) toast.success(`${count} participant(s) mis en cache`);
    } catch { toast.error("Impossible de mettre en cache"); }
  }, [token]);

  useEffect(() => { cacheParticipants(); }, [cacheParticipants]);

  // Auto-sync when coming back online
  useEffect(() => {
    if (isOnline && offlineQueue.length > 0) syncOfflineScans();
  }, [isOnline]);

  const syncOfflineScans = async () => {
    const queue = getQueue();
    if (queue.length === 0) return;
    setSyncing(true);
    let synced = 0;
    const remaining = [];
    for (const item of queue) {
      try {
        await axios.post(`${API}/api/event/scan`, { qr_data: item.qr_data }, { headers });
        synced++;
      } catch { remaining.push(item); }
    }
    setQueue(remaining);
    setOfflineQueue(remaining);
    setSyncing(false);
    if (synced > 0) toast.success(`${synced} scan(s) synchronise(s)`);
    if (remaining.length > 0) toast.error(`${remaining.length} en attente`);
  };

  // ===== CAMERA MANAGEMENT =====
  const startCamera = useCallback(async () => {
    if (html5QrRef.current) {
      try { await html5QrRef.current.stop(); } catch {}
    }
    setCameraError(null);
    try {
      const { Html5Qrcode } = await import("html5-qrcode");
      const scanner = new Html5Qrcode("qr-reader");
      html5QrRef.current = scanner;

      await scanner.start(
        { facingMode },
        { fps: 15, qrbox: fullscreen ? { width: 280, height: 280 } : { width: 250, height: 250 }, aspectRatio: fullscreen ? undefined : 1 },
        (decodedText) => {
          const now = Date.now();
          if (decodedText === lastScanRef.current.data && now - lastScanRef.current.time < SCAN_COOLDOWN) return;
          lastScanRef.current = { data: decodedText, time: now };
          handleScan(decodedText);
        },
        () => {}
      );
      setCameraActive(true);
    } catch (err) {
      console.error("Camera error:", err);
      setCameraError(
        err.name === "NotAllowedError" ? "Acces a la camera refuse. Autorisez l'acces dans les parametres de votre navigateur."
        : err.name === "NotFoundError" ? "Aucune camera trouvee sur cet appareil."
        : "Impossible d'acceder a la camera."
      );
      setMode("manual");
    }
  }, [facingMode, fullscreen]);

  const stopCamera = useCallback(async () => {
    if (html5QrRef.current) {
      try { await html5QrRef.current.stop(); html5QrRef.current = null; } catch {}
    }
    setCameraActive(false);
  }, []);

  useEffect(() => {
    if (mode === "camera") {
      startCamera();
    } else {
      stopCamera();
      setTimeout(() => inputRef.current?.focus(), 200);
    }
    return () => { stopCamera(); };
  }, [mode, facingMode]);

  // ===== TORCH / FLASH =====
  const toggleTorch = async () => {
    try {
      if (html5QrRef.current) {
        const track = html5QrRef.current.getRunningTrackSettings?.();
        if (track) {
          const capabilities = html5QrRef.current.getRunningTrackCameraCapabilities?.();
          if (capabilities?.torchFeature?.isSupported()) {
            await capabilities.torchFeature.apply(!torchOn);
            setTorchOn(!torchOn);
          } else {
            toast.error("Flash non disponible sur cet appareil");
          }
        }
      }
    } catch { toast.error("Flash non disponible"); }
  };

  // ===== CAMERA FLIP =====
  const flipCamera = async () => {
    await stopCamera();
    setFacingMode(prev => prev === "environment" ? "user" : "environment");
  };

  // ===== FULLSCREEN =====
  const toggleFullscreen = () => {
    if (!fullscreen) {
      const el = containerRef.current || document.documentElement;
      if (el.requestFullscreen) el.requestFullscreen().catch(() => {});
      else if (el.webkitRequestFullscreen) el.webkitRequestFullscreen();
    } else {
      if (document.exitFullscreen) document.exitFullscreen().catch(() => {});
      else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
    }
    setFullscreen(!fullscreen);
  };

  useEffect(() => {
    const handler = () => setFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", handler);
    return () => document.removeEventListener("fullscreenchange", handler);
  }, []);

  // ===== SCAN HANDLER =====
  const handleScan = useCallback(async (qrData) => {
    if (!qrData || scanning) return;
    setScanning(true);
    setScanResult(null);

    if (isOnline) {
      try {
        const { data } = await axios.post(`${API}/api/event/scan`, { qr_data: qrData }, { headers });
        setScanResult(data);
        setHistory(h => [{ ...data, time: new Date().toLocaleTimeString("fr-FR"), qr: qrData }, ...h].slice(0, 50));
        setScanCount(c => ({
          total: c.total + 1,
          authorized: c.authorized + (data.status === "authorized" ? 1 : 0),
          rejected: c.rejected + (["invalid", "unauthorized", "cancelled"].includes(data.status) ? 1 : 0),
        }));
        if (navigator.vibrate) {
          if (data.status === "authorized") navigator.vibrate(200);
          else if (data.status === "already_checked") navigator.vibrate([100, 50, 100]);
          else navigator.vibrate([200, 100, 200]);
        }
      } catch (e) {
        if (!e.response) {
          handleOfflineScan(qrData);
        } else {
          const errData = { status: "invalid", message: e.response?.data?.detail || "Erreur de scan" };
          setScanResult(errData);
        }
      }
    } else {
      handleOfflineScan(qrData);
    }

    setScanning(false);
    setQrInput("");
    if (mode === "manual") setTimeout(() => inputRef.current?.focus(), 300);
    // Auto-clear result after 4 seconds
    setTimeout(() => setScanResult(prev => prev?.status === "authorized" || prev?.status === "already_checked" || prev?.status === "invalid" ? null : prev), 4000);
  }, [scanning, token, mode, isOnline]);

  const handleOfflineScan = (qrData) => {
    const cache = getCache();
    const participant = cache[qrData];
    const offScans = getOfflineScans();

    if (!participant) {
      const result = { status: "invalid", message: "QR non reconnu (hors ligne)" };
      setScanResult(result);
      if (navigator.vibrate) navigator.vibrate([200, 100, 200]);
      return;
    }

    const alreadyScanned = offScans.includes(qrData);
    if (alreadyScanned || participant.status === "checked_in") {
      const result = {
        status: "offline_dup", message: "Deja scanne (hors ligne)",
        participant: { first_name: participant.first_name, last_name: participant.last_name, reference: participant.reference, category: participant.category, is_vip: participant.is_vip },
      };
      setScanResult(result);
      if (navigator.vibrate) navigator.vibrate([100, 50, 100]);
    } else {
      setOfflineScans([...offScans, qrData]);
      const queue = getQueue();
      queue.push({ qr_data: qrData, scanned_at: new Date().toISOString() });
      setQueue(queue);
      setOfflineQueue(queue);

      const result = {
        status: "offline_ok", message: "Valide hors ligne — sera synchronise",
        participant: { first_name: participant.first_name, last_name: participant.last_name, reference: participant.reference, category: participant.category, is_vip: participant.is_vip },
      };
      setScanResult(result);
      if (navigator.vibrate) navigator.vibrate(200);
    }

    setHistory(h => [{ status: participant ? "offline_ok" : "invalid", participant: participant || null, time: new Date().toLocaleTimeString("fr-FR"), qr: qrData }, ...h].slice(0, 50));
    setScanCount(c => ({ total: c.total + 1, authorized: c.authorized + (participant && !offScans?.includes(qrData) ? 1 : 0), rejected: c.rejected + (!participant ? 1 : 0) }));
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && qrInput.trim()) handleScan(qrInput.trim());
  };

  const reset = () => { setScanResult(null); setQrInput(""); lastScanRef.current = { data: "", time: 0 }; if (mode === "manual") inputRef.current?.focus(); };

  const resultStyle = scanResult ? RESULT_STYLES[scanResult.status] || RESULT_STYLES.invalid : null;

  // ===== FULLSCREEN MOBILE LAYOUT =====
  if (fullscreen) {
    return (
      <div ref={containerRef} className="fixed inset-0 z-[100] bg-black flex flex-col" data-testid="scanner-fullscreen">
        {/* Top bar */}
        <div className="flex items-center justify-between px-4 py-3 bg-black/80 backdrop-blur-sm z-10">
          <div className="flex items-center gap-3">
            <QrCode className="h-5 w-5 text-[#0099CC]" />
            <span className="text-white font-bold text-sm">Scanner</span>
          </div>
          <div className="flex items-center gap-3 text-xs">
            <span className="text-[#10B981] font-bold">{scanCount.authorized}</span>
            <span className="text-[#64748b]">/</span>
            <span className="text-white">{scanCount.total}</span>
            {!isOnline && <WifiOff className="h-4 w-4 text-[#F59E0B]" />}
          </div>
        </div>

        {/* Camera */}
        <div className="flex-1 relative">
          <div id="qr-reader" className="w-full h-full" style={{ minHeight: "100%" }} />

          {/* Scan result overlay */}
          {scanResult && resultStyle && (
            <div className="absolute inset-0 flex items-center justify-center z-20 animate-in fade-in duration-200" data-testid="scan-result-fullscreen">
              <div className={`${resultStyle.bg} rounded-3xl p-8 mx-6 text-white text-center shadow-2xl max-w-sm w-full`}>
                <resultStyle.icon className="h-16 w-16 mx-auto mb-4" />
                <p className="text-2xl font-black tracking-wide mb-2">{resultStyle.label}</p>
                {scanResult.participant && (
                  <div className="bg-white/15 rounded-2xl p-4 mt-4 space-y-1">
                    <p className="text-xl font-bold">{scanResult.participant.first_name} {scanResult.participant.last_name}</p>
                    {scanResult.participant.is_vip && <span className="inline-block text-xs bg-white/25 px-3 py-1 rounded-full">VIP</span>}
                    {scanResult.participant.reference && <p className="text-sm opacity-80 font-mono">{scanResult.participant.reference}</p>}
                    {scanResult.participant.organization && <p className="text-sm opacity-70">{scanResult.participant.organization}</p>}
                  </div>
                )}
                {scanResult.checked_in_at && (
                  <p className="text-sm opacity-75 mt-3">Premier passage : {new Date(scanResult.checked_in_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}</p>
                )}
                {scanResult.message && !scanResult.participant && <p className="text-lg opacity-90 mt-2">{scanResult.message}</p>}
              </div>
            </div>
          )}
        </div>

        {/* Bottom controls */}
        <div className="flex items-center justify-around px-4 py-5 bg-black/80 backdrop-blur-sm z-10 gap-3">
          <button onClick={toggleTorch} className={`p-4 rounded-full ${torchOn ? "bg-[#F59E0B] text-black" : "bg-white/10 text-white"} active:scale-95 transition-all`} data-testid="torch-btn">
            <Flashlight className="h-6 w-6" />
          </button>
          <button onClick={flipCamera} className="p-4 rounded-full bg-white/10 text-white active:scale-95 transition-all" data-testid="flip-camera-btn">
            <SwitchCamera className="h-6 w-6" />
          </button>
          <button onClick={reset} className="p-4 rounded-full bg-white/10 text-white active:scale-95 transition-all" data-testid="reset-btn">
            <RotateCcw className="h-6 w-6" />
          </button>
          <button onClick={toggleFullscreen} className="p-4 rounded-full bg-red-500/80 text-white active:scale-95 transition-all" data-testid="exit-fullscreen-btn">
            <Minimize className="h-6 w-6" />
          </button>
        </div>
      </div>
    );
  }

  // ===== NORMAL LAYOUT =====
  return (
    <div ref={containerRef} className="max-w-2xl mx-auto space-y-4" data-testid="org-scanner-page">
      {/* Header with stats */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-white flex items-center gap-2">
            <QrCode className="h-6 w-6 text-[#0099CC]" />
            Scanner QR
          </h1>
          <p className="text-sm text-[#64748b]">Controlez les entrees de vos participants</p>
        </div>
        <div className="flex items-center gap-4 text-sm">
          <div className="text-center"><span className="text-lg font-bold text-white">{scanCount.total}</span><p className="text-[10px] text-[#64748b]">Scans</p></div>
          <div className="text-center"><span className="text-lg font-bold text-[#10B981]">{scanCount.authorized}</span><p className="text-[10px] text-[#64748b]">Valides</p></div>
          <div className="text-center"><span className="text-lg font-bold text-red-400">{scanCount.rejected}</span><p className="text-[10px] text-[#64748b]">Refuses</p></div>
        </div>
      </div>

      {/* Offline indicator */}
      <div className={`flex items-center justify-between p-3 rounded-xl border ${isOnline ? "bg-[#10B981]/10 border-[#10B981]/30" : "bg-[#F59E0B]/10 border-[#F59E0B]/30"}`} data-testid="connectivity-status">
        <div className="flex items-center gap-2">
          {isOnline ? <Wifi className="h-4 w-4 text-[#10B981]" /> : <WifiOff className="h-4 w-4 text-[#F59E0B]" />}
          <span className={`text-sm font-medium ${isOnline ? "text-[#10B981]" : "text-[#F59E0B]"}`}>
            {isOnline ? "En ligne" : "Hors ligne"}{cacheReady && !isOnline ? " — mode cache actif" : ""}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {offlineQueue.length > 0 && (
            <span className="text-xs text-[#F59E0B] bg-[#F59E0B]/10 px-2 py-1 rounded-lg">{offlineQueue.length} en attente</span>
          )}
          {isOnline && offlineQueue.length > 0 && (
            <button data-testid="sync-btn" onClick={syncOfflineScans} disabled={syncing} className="text-xs text-[#0099CC] hover:text-white flex items-center gap-1 transition-colors">
              <RefreshCw className={`h-3 w-3 ${syncing ? "animate-spin" : ""}`} /> Sync
            </button>
          )}
          {isOnline && (
            <button onClick={cacheParticipants} className="text-xs text-[#64748b] hover:text-white transition-colors">Actualiser cache</button>
          )}
        </div>
      </div>

      {/* Mode Toggle + Fullscreen */}
      <div className="flex gap-2">
        <div className="flex flex-1 bg-[#0B0F19] border border-[#1E293B] rounded-xl p-1 gap-1">
          <button
            data-testid="mode-camera-btn"
            onClick={() => setMode("camera")}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium transition-all ${mode === "camera" ? "bg-[#0099CC] text-white" : "text-[#64748b] hover:text-white"}`}
          >
            <Camera className="h-4 w-4" /> Camera
          </button>
          <button
            data-testid="mode-manual-btn"
            onClick={() => setMode("manual")}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium transition-all ${mode === "manual" ? "bg-[#0099CC] text-white" : "text-[#64748b] hover:text-white"}`}
          >
            <Keyboard className="h-4 w-4" /> Saisie manuelle
          </button>
        </div>
        {mode === "camera" && (
          <button
            data-testid="fullscreen-btn"
            onClick={toggleFullscreen}
            className="flex items-center gap-1.5 px-4 py-2.5 bg-[#0B0F19] border border-[#1E293B] rounded-xl text-[#94A3B8] hover:text-white hover:border-[#0099CC]/50 text-sm font-medium transition-all"
          >
            <Maximize className="h-4 w-4" /> Plein ecran
          </button>
        )}
      </div>

      {/* Camera View */}
      {mode === "camera" && (
        <div className="bg-[#0B0F19] border border-[#1E293B] rounded-2xl overflow-hidden">
          <div id="qr-reader" className="w-full" style={{ minHeight: 300 }} />
          {cameraError && (
            <div className="flex flex-col items-center justify-center py-12 px-6 text-center">
              <CameraOff className="h-12 w-12 text-red-400 mb-4" />
              <p className="text-red-400 font-medium mb-2">Camera indisponible</p>
              <p className="text-[#94A3B8] text-sm">{cameraError}</p>
              <Button onClick={() => { setCameraError(null); startCamera(); }} variant="outline" className="mt-4 border-[#2A3441] text-[#94A3B8]">
                <RotateCcw className="h-4 w-4 mr-1.5" /> Reessayer
              </Button>
            </div>
          )}
          {!cameraActive && !cameraError && (
            <div className="flex flex-col items-center justify-center py-12 px-4">
              <CameraOff className="h-12 w-12 text-[#2A3441] mb-4" />
              <p className="text-[#64748b] text-center text-sm">Chargement de la camera...</p>
              <p className="text-[#4A5568] text-center text-xs mt-1">Autorisez l'acces a la camera si demande</p>
            </div>
          )}
          {/* Camera controls */}
          {cameraActive && (
            <div className="flex items-center justify-center gap-3 p-3 border-t border-[#1E293B]">
              <button onClick={toggleTorch} className={`p-2.5 rounded-xl ${torchOn ? "bg-[#F59E0B] text-black" : "bg-[#151B2B] text-[#94A3B8] hover:text-white"} transition-all`} data-testid="torch-btn-normal" title="Flash">
                <Flashlight className="h-5 w-5" />
              </button>
              <button onClick={flipCamera} className="p-2.5 rounded-xl bg-[#151B2B] text-[#94A3B8] hover:text-white transition-all" data-testid="flip-camera-btn-normal" title="Changer de camera">
                <SwitchCamera className="h-5 w-5" />
              </button>
              <button onClick={reset} className="p-2.5 rounded-xl bg-[#151B2B] text-[#94A3B8] hover:text-white transition-all" title="Relancer">
                <RotateCcw className="h-5 w-5" />
              </button>
            </div>
          )}
        </div>
      )}

      {/* Manual Input */}
      {mode === "manual" && (
        <div className="bg-[#0B0F19] border border-[#1E293B] rounded-2xl p-5">
          <label className="text-sm text-[#94A3B8] mb-2 block">Scannez ou saisissez le code QR</label>
          <div className="flex gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#4A5568]" />
              <Input
                ref={inputRef}
                data-testid="qr-scan-input"
                value={qrInput}
                onChange={(e) => setQrInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="event_id:qr_token"
                className="pl-10 bg-[#151B2B] border-[#2A3441] text-white h-12 text-base font-mono"
                autoFocus
              />
            </div>
            <Button
              data-testid="scan-submit-btn"
              onClick={() => handleScan(qrInput.trim())}
              disabled={!qrInput.trim() || scanning}
              className="bg-[#0099CC] hover:bg-[#00AADB] text-white h-12 px-6"
            >
              {scanning ? "..." : "Verifier"}
            </Button>
          </div>
        </div>
      )}

      {/* Scan Result (normal mode) */}
      {scanResult && resultStyle && (
        <div data-testid="scan-result" className={`${resultStyle.bg} rounded-2xl p-5 text-white transition-all animate-in fade-in duration-300`}>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <resultStyle.icon className="h-8 w-8" />
              <span className="text-xl font-bold tracking-wide">{resultStyle.label}</span>
            </div>
            <button onClick={reset} className="bg-white/20 hover:bg-white/30 rounded-full p-2"><RotateCcw className="h-5 w-5" /></button>
          </div>
          {scanResult.message && <p className="text-sm opacity-90 mb-3">{scanResult.message}</p>}
          {scanResult.participant && (
            <div className="bg-white/10 rounded-xl p-4 space-y-1.5">
              <div className="flex items-center gap-2">
                <User className="h-4 w-4 opacity-70" />
                <span className="font-semibold text-lg">{scanResult.participant.first_name} {scanResult.participant.last_name}</span>
                {(scanResult.participant.is_vip || scanResult.participant.category === "vip") && <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full">VIP</span>}
              </div>
              {scanResult.participant.reference && <div className="text-sm opacity-80 font-mono">{scanResult.participant.reference}</div>}
              {scanResult.participant.organization && <div className="text-sm opacity-70">{scanResult.participant.organization}</div>}
            </div>
          )}
          {scanResult.checked_in_at && (
            <p className="text-sm opacity-75 mt-2">Premier passage : {new Date(scanResult.checked_in_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}</p>
          )}
        </div>
      )}

      {/* History */}
      {history.length > 0 && (
        <div className="bg-[#0B0F19] border border-[#1E293B] rounded-xl">
          <div className="flex items-center justify-between p-3 border-b border-[#1E293B]">
            <h2 className="font-semibold text-white text-sm flex items-center gap-2"><History className="h-4 w-4 text-[#64748b]" />Historique ({history.length})</h2>
            <button onClick={() => { setHistory([]); setScanCount({ total: 0, authorized: 0, rejected: 0 }); }} className="text-xs text-[#64748b] hover:text-white">Effacer</button>
          </div>
          <div className="divide-y divide-[#1E293B]/50 max-h-48 overflow-y-auto">
            {history.map((h, i) => {
              const s = RESULT_STYLES[h.status] || RESULT_STYLES.invalid;
              return (
                <div key={i} className="flex items-center gap-3 p-2.5 text-sm">
                  <s.icon className={`h-4 w-4 shrink-0`} style={{ color: s.color }} />
                  <div className="flex-1 min-w-0">
                    <span className="text-white font-medium">{h.participant ? `${h.participant.first_name} ${h.participant.last_name}` : "—"}</span>
                  </div>
                  <span className="text-[10px] text-[#4A5568] shrink-0">{h.time}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
